/**
 * The live run, inside the work-item pane (YAG-373, prototype `#/run482`).
 *
 * The delegated coding run stops being a black box here: the plan an editor
 * approved, drawn as steps; the commits the attempt recorded; and the log,
 * live. It fills the seam Task 7 left in `CaseFile`.
 *
 * Three honesty rules shape it, and they are why it does not look exactly like
 * the prototype:
 *
 *   1. Ticks are earned. Only a mission-native run journals per-step status
 *      (`run_steps`), and the read says which source it used. When the source
 *      does not track progress, every step renders plain and the pane SAYS the
 *      run does not report step by step. A checkmark nobody wrote down is the
 *      exact failure this surface exists to remove.
 *   2. Commits are not pinned to steps. `CodingEvent` carries one commit sha
 *      per attempt and no commit message at all, so there is no step-to-commit
 *      link in the data. The trail is its own list, under the steps.
 *   3. A plan that cannot be read is not a broken steps UI. It is the log,
 *      plus one plain line saying the run is running the approved plan.
 *   4. The kind word and the outputs are read, never inferred. The word beside
 *      the worker is `runs.purpose` (planning / execution / scoping) and the
 *      "what it produced" block is `runs.artifacts`, normalized server-side to
 *      a label, a url and a PR number. Neither is guessed from the log, and a
 *      run that recorded no outputs renders no block: no record, no row. This
 *      is also why a scoping run does not look broken here. It has no steps
 *      because no plan exists yet, it says so in those words, and the outputs
 *      block sits empty rather than showing an absence of code as a failure.
 *

 * Liveness rides what already exists: `useRunEvents` backfills over REST and
 * appends from the `coding_event` WS stream; the steps re-read on the
 * `runs.updated` / `runs.created` window events `useWebSocket` already
 * dispatches. No new protocol, no polling.
 */
import { useCallback, useEffect, useState } from "react";
import { captureUiError } from "../sentry.js";
import type { CodingEvent, WorkRunViewResponse } from "@yagni/shared";
import { useRunEvents } from "../hooks/useRunEvents.js";
import { stripMarkdownInline } from "../lib/markdown.js";
import { FanoutSection } from "./FanoutSection.js";
import { PrChip } from "./PrChip.js";
import {
  RUN_COMMITS_LABEL,
  RUN_COMMITS_NOTE,
  RUN_LABEL,
  RUN_LIVE_NOTE,
  RUN_LOG_EMPTY,
  RUN_LOG_LABEL,
  RUN_NO_STEPS_BY_PURPOSE,
  RUN_NO_STEPS_NOTE,
  RUN_OUTPUTS_LABEL,
  RUN_PURPOSE_LABEL,
  RUN_SETTLED_NOTE,
  RUN_STEPS_UNTRACKED_NOTE,
  runStartedLine,
} from "./workCopy.js";

/** How many log lines the trail shows. The full transcript is in Provenance. */
export const LOG_TRAIL_LINES = 12;

const EMPTY_VIEW: WorkRunViewResponse = { run: null, stepSource: null, steps: [] };

/** Read the run view. Only asked for while an item is actually mid-run. */
export function useWorkRunView(workItemId: string, enabled: boolean) {
  const [view, setView] = useState<WorkRunViewResponse>(EMPTY_VIEW);
  const [loading, setLoading] = useState(false);
  // A failed read leaves the view empty. Callers that print a fact from the
  // view (what the attempt kept) say it could not be read instead.
  const [failed, setFailed] = useState(false);
  const [nonce, setNonce] = useState(0);
  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!enabled || !workItemId) {
      setView(EMPTY_VIEW);
      setFailed(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    // `nonExecutionRun=1` asks the route to name a planning or scoping run on an
    // item that never executed, so this surface can say what DID happen instead
    // of drawing nothing. Opt-in on purpose: the default answer is unchanged for
    // any bundle deployed before it existed.
    fetch(`/api/work-items/${encodeURIComponent(workItemId)}/run-view?nonExecutionRun=1`, {
      credentials: "include",
    })
      .then((res) => {
        if (!res.ok) throw new Error(`run view read failed (HTTP ${res.status})`);
        return res.json() as Promise<WorkRunViewResponse>;
      })
      .then((body) => {
        if (cancelled) return;
        setView(body && Array.isArray(body.steps) ? body : EMPTY_VIEW);
        setFailed(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setView(EMPTY_VIEW);
        setFailed(true);
        captureUiError(error, { area: "Work", operation: "read_run_view" });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [workItemId, enabled, nonce]);

  // The existing run stream. A status change on THIS item re-reads the steps,
  // which is how a mission step flipping to succeeded reaches the screen.
  useEffect(() => {
    if (!enabled) return;
    const onRun = (event: Event) => {
      const run = (event as CustomEvent<{ run?: { work_item_id?: string } }>).detail?.run;
      if (run?.work_item_id !== workItemId) return;
      reload();
    };
    window.addEventListener("runs.updated", onRun);
    window.addEventListener("runs.created", onRun);
    return () => {
      window.removeEventListener("runs.updated", onRun);
      window.removeEventListener("runs.created", onRun);
    };
  }, [workItemId, enabled, reload]);

  return { view, loading, reload, failed };
}

function clockOf(at: number): string {
  const when = new Date(at);
  if (Number.isNaN(when.getTime())) return "";
  return when.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

export interface RunLogLine {
  key: string;
  clock: string;
  text: string;
}

/**
 * The mono trail: one line per event, newest last, bounded.
 *
 * Every line is the event's own words. Nothing is summarised into a sentence
 * the run did not produce, and an event kind with nothing readable to say
 * (usage counters, bare thinking ticks) is left out rather than padded into
 * filler.
 */
export function runLogLines(events: CodingEvent[], limit = LOG_TRAIL_LINES): RunLogLine[] {
  const lines: RunLogLine[] = [];
  events.forEach((event, index) => {
    // A prose turn is markdown; a mono line wants its words, not its marks.
    const raw = event.kind === "message" ? stripMarkdownInline(event.text) : eventLineText(event);
    const trimmed = raw?.replace(/\s+/g, " ").trim();
    if (!trimmed) return;
    lines.push({ key: `${index}-${event.at}`, clock: clockOf(event.at), text: trimmed });
  });
  return lines.slice(-limit);
}

/** A pipeline stage's beat in plain words. Stage keys are machine names, never copy. */
const STAGE_WORDS: Record<string, { start: string; finish: string }> = {
  map: { start: "Reading the repository", finish: "Read the repository" },
  plan: { start: "Planning the change", finish: "Planned the change" },
  implement: { start: "Starting the build", finish: "Finished the build pass" },
  review: { start: "Reviewing the change", finish: "Reviewed the change" },
  fix: { start: "Fixing what the review found", finish: "Fixed what the review found" },
  leg: { start: "Started working", finish: "Stopped working" },
};

/**
 * "Reviewing the change (round 2)", "Reviewed the change: 3 findings". A stage
 * this bundle does not know still reads as a sentence, never as its key.
 */
export function stagePhrase(event: {
  stage: string;
  phase: string;
  round?: number;
  findings?: number;
}): string {
  const words = STAGE_WORDS[event.stage] ?? {
    start: "Starting the next step",
    finish: "Finished a step",
  };
  const base = event.phase === "finish" ? words.finish : words.start;
  const rounds = event.stage === "review" || event.stage === "fix";
  const round = rounds && event.round ? ` (round ${event.round})` : "";
  const findings =
    event.stage === "review" && event.phase === "finish" && typeof event.findings === "number"
      ? `: ${event.findings} finding${event.findings === 1 ? "" : "s"}`
      : "";
  return `${base}${round}${findings}`;
}

/**
 * One event as one line, in the event's own words. Null for a kind with
 * nothing readable to say (a usage counter, a bare thinking tick). Shared by
 * the mono trail and by the conversation view's note rows, so the two never
 * describe the same event differently.
 */
export function eventLineText(event: CodingEvent): string | null {
  switch (event.kind) {
    case "tool_call":
      return `${event.tool} ${event.argsSummary}`.trim();
    case "tool_result":
      // A search that found nothing is neutral, not a failure: the line must
      // carry no "failed" substring, or the note path reddens it.
      return event.outcome === "no_match"
        ? `${event.tool} no matches`
        : `${event.tool} ${event.ok ? "ok" : "failed"} · ${event.summary}`.trim();
    case "message":
      return event.text;
    case "stage":
      return stagePhrase(event);
    case "awaiting_review":
      return `waiting on a review of the ${event.subject}`;
    case "review_posted":
      return `review posted · ${event.approved ? "approved" : "changes requested"}`;
    case "completed":
      return `finished on ${event.branch}${event.commitSha ? ` at ${event.commitSha.slice(0, 7)}` : ""}`;
    case "error":
      return `error · ${event.message}`;
    case "plan_proposed":
    case "plan_revised":
      return `plan ${event.kind === "plan_proposed" ? "proposed" : "revised"} · ${event.plan.summary}`;
    default:
      // thinking / usage: the trail is what the run DID. What it thought is
      // the conversation view's to fold (`LiveTalk`), never a log line.
      return null;
  }
}

export interface RunCommit {
  key: string;
  sha: string;
  branch: string;
  clock: string;
  prUrl: string | null;
}

/** The commits the attempt recorded. One per `completed` event that had a sha. */
export function runCommits(events: CodingEvent[]): RunCommit[] {
  const out: RunCommit[] = [];
  events.forEach((event, index) => {
    if (event.kind !== "completed" || !event.commitSha) return;
    out.push({
      key: `${index}-${event.commitSha}`,
      sha: event.commitSha,
      branch: event.branch,
      clock: clockOf(event.at),
      prUrl: event.prUrl,
    });
  });
  return out;
}

export function RunSurface({
  workItemId,
  workerName,
  autonomyLevel,
  live,
}: {
  workItemId: string;
  /** The worker whose run this is. Null keeps the header neutral. */
  workerName: string | null;
  /** The Team's earned level, as a chip. Never a tier (banned word and idea). */
  autonomyLevel?: string | null;
  /** False once the attempt has stopped: the note changes, the log stays. */
  live: boolean;
}) {
  const { view } = useWorkRunView(workItemId, true);
  const events = useRunEvents(workItemId);
  const log = runLogLines(events);
  const commits = runCommits(events);
  const started = runStartedLine(view.run?.startedAt ?? null);
  // Only a mission-native read knows which step is which. Everything else shows
  // the approved plan and says it cannot say more.
  const tracked = view.stepSource === "mission";
  // The three read-only facts the run row carries about itself. Absent on a
  // response from a backend that predates them, which reads as "not recorded"
  // and prints nothing.
  const purpose = view.run?.purpose ?? null;
  const purposeWord = purpose ? RUN_PURPOSE_LABEL[purpose] : null;
  // 'code' is the default on every row and on every coding run, so printing it
  // would be noise on the only surface where it is already obvious.
  const kind = view.run?.kind && view.run.kind !== "code" ? view.run.kind : null;
  const outputs = view.run?.artifacts ?? [];

  return (
    <div
      className="wk-run"
      data-testid="run-surface"
      data-step-source={view.stepSource ?? "none"}
      data-run-purpose={purpose ?? "none"}
    >
      <div className="wk-run-head">
        <span className="cf-sect wk-run-lab">{RUN_LABEL}</span>
        {workerName && (
          <span className="wk-run-worker" data-testid="run-worker">
            {workerName}
          </span>
        )}
        {purposeWord && (
          // The pair of classes is deliberate: the second is the style that
          // exists today, the first is the hook for a rule of its own once the
          // stylesheet gains one.
          <span className="wk-run-purpose wk-run-worker" data-testid="run-purpose">
            {purposeWord}
          </span>
        )}
        {kind && (
          // A machine fact, in the machine voice, and only when it is not the
          // default.
          <span className="wk-run-kind wk-run-started" data-testid="run-kind">
            {kind}
          </span>
        )}
        {started && (
          <span className="wk-run-started" data-testid="run-started">
            {started}
          </span>
        )}
        <span className="wk-run-spacer" />
        {autonomyLevel && (
          <span className="wk-run-autonomy" data-testid="run-autonomy">
            {autonomyLevel}
          </span>
        )}
      </div>

      <p className="wk-run-note">{live ? RUN_LIVE_NOTE : RUN_SETTLED_NOTE}</p>

      {view.steps.length > 0 ? (
        <>
          <ol className="wk-run-steps" data-testid="run-steps">
            {view.steps.map((step) => (
              <li
                key={step.sequence}
                className={`wk-run-step is-${step.state.replace("_", "-")}`}
                data-testid={`run-step-${step.sequence}`}
                data-state={step.state}
              >
                <span className="wk-run-step-mark" aria-hidden>
                  {step.state === "done" ? "✓" : step.sequence}
                </span>
                <span className="wk-run-step-body">
                  <span className="wk-run-step-desc">{step.description}</span>
                  {step.stage && <span className="wk-run-step-stage">{step.stage}</span>}
                </span>
              </li>
            ))}
          </ol>
          {!tracked && (
            <p className="wk-run-untracked" data-testid="run-steps-untracked">
              {RUN_STEPS_UNTRACKED_NOTE}
            </p>
          )}
        </>
      ) : (
        // A plan that cannot be read is never a broken steps UI. Neither is a
        // run that never had plan steps to draw, and the two say so
        // differently.
        <p className="wk-run-nosteps" data-testid="run-no-steps">
          {purpose ? RUN_NO_STEPS_BY_PURPOSE[purpose] : RUN_NO_STEPS_NOTE}
        </p>
      )}

      {/* How the build stage split, when it recorded a split. Nothing renders
          for a run that never did, which is every run before the diamond. */}
      <FanoutSection events={events} />

      {/* What the run produced, from `runs.artifacts`. Outputs are listed, not
          pinned to steps: the artifact records the run that made it and never
          the step, so a step-level attribution would be invented. An empty
          list renders nothing, which is the honest answer for a scoping run and
          for a document run whose outputs live in another table entirely. */}
      {outputs.length > 0 && (
        <div className="wk-run-outputs wk-run-commits" data-testid="run-outputs">
          <span className="wk-run-sub">{RUN_OUTPUTS_LABEL}</span>
          <ul className="wk-run-output-list wk-run-commit-list">
            {outputs.map((artifact, index) => (
              <li
                key={`${index}-${artifact.kind}-${artifact.url ?? artifact.label}`}
                data-artifact-kind={artifact.kind}
              >
                {artifact.kind === "pr" ? (
                  // One way to spell a pull request across the whole surface.
                  <PrChip prUrl={artifact.url} prNumber={artifact.prNumber ?? null} />
                ) : artifact.url ? (
                  <a
                    className="cf-output-preview-link"
                    href={artifact.url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {artifact.label}
                  </a>
                ) : (
                  // No url recorded, so no link is offered. The label is the
                  // whole truth about it.
                  <span className="wk-run-output-label wk-run-sha">{artifact.label}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {commits.length > 0 && (
        <div className="wk-run-commits" data-testid="run-commits">
          <span className="wk-run-sub">{RUN_COMMITS_LABEL}</span>
          <ul className="wk-run-commit-list">
            {commits.map((commit) => (
              <li key={commit.key}>
                <span className="wk-run-sha">{commit.sha.slice(0, 7)}</span>
                <span className="wk-run-branch">{commit.branch}</span>
                {commit.clock && <span className="wk-run-clock">{commit.clock}</span>}
              </li>
            ))}
          </ul>
          <p className="wk-run-untracked">{RUN_COMMITS_NOTE}</p>
        </div>
      )}

      <div className="wk-run-log" data-testid="run-log">
        <span className="wk-run-sub">{RUN_LOG_LABEL}</span>
        {log.length > 0 ? (
          <pre className="wk-run-code">
            {log.map((line) => (
              <span key={line.key} className="wk-run-line">
                {line.clock ? `${line.clock} · ` : ""}
                {line.text}
                {"\n"}
              </span>
            ))}
          </pre>
        ) : (
          <p className="wk-run-untracked" data-testid="run-log-empty">
            {RUN_LOG_EMPTY}
          </p>
        )}
      </div>
    </div>
  );
}
